/**
 * Lane C (#580) — cost/abuse policy + non-LLM fallbacks.
 * Client mirrors what the backend enforces; UI never sees a thrown error.
 */
import type { AskPamResult, SimulationBeatResult } from './types';

export const ASK_PAM_PROMPT_MAX = 500;

/** Soft client ceilings; server is authoritative when callable is live. */
export const SESSION_RATE_LIMIT = { max: 8, windowMs: 60_000 };
export const IP_RATE_LIMIT = { max: 20, windowMs: 60_000 };

export interface SimulationDecision {
  id: string;
  label: string;
}

export interface SimulationBeatContent {
  scenarioId: string;
  title: string;
  stem: string;
  decisions: SimulationDecision[];
  /** Non-LLM feedback keyed by decision id. */
  feedbackByDecision: Record<string, string>;
  defaultFeedback: string;
}

/** One decision point reused from the Maria grief sim (exam-style FIRST). */
export const PUBLIC_DIAGNOSTIC_BEAT: SimulationBeatContent = {
  scenarioId: 'maria-grief',
  title: 'Grief and loss: your first move',
  stem:
    "Maria Santos, 45, was referred by her PCP for persistent sadness after her husband's death. " +
    'In the waiting room she quietly says she sometimes wishes she "would not wake up," but she has no plan. ' +
    'You are about to begin the intake. What should you do FIRST?',
  decisions: [
    {
      id: 'safety-first',
      label: 'Gently explore suicidal ideation, intent, and protective factors before other history',
    },
    {
      id: 'rapport-only',
      label: 'Focus only on building rapport and save any safety questions for a later session',
    },
    {
      id: 'grief-psychoeducation',
      label: 'Start with grief psychoeducation so she feels the referral was worthwhile',
    },
    {
      id: 'call-pcp',
      label: 'End the intake and call her PCP immediately before speaking further with Maria',
    },
  ],
  feedbackByDecision: {
    'safety-first':
      "Exam logic: safety first. Passive ideation still needs a calm, direct assessment of intent, plan, means, and supports before you widen the interview. You began the clinical simulation the way the ASWB wants: prioritize safety, then continue the intake.",
    'rapport-only':
      'Building rapport matters, but the exam treats passive suicidal ideation as a cue to assess safety in this session, not to postpone it. Next time, pair warmth with a direct safety check before moving on.',
    'grief-psychoeducation':
      'Psychoeducation helps later. With ideation on the table, the FIRST move is a focused safety assessment, then you can teach and support.',
    'call-pcp':
      'Collaboration with the PCP can be appropriate, but ending the intake before assessing Maria skips the social worker’s immediate duty to evaluate risk with the client present.',
  },
  defaultFeedback:
    'In this beat, prioritize a calm safety assessment when ideation is disclosed, then continue the intake. That sequencing is what the exam rewards.',
};

const EXAM_PREP_HINTS =
  /\b(aswb|exam|lmsw|lcsw|practice question|study|test prep|nasw|content area|first|next|best)\b/i;

const CLINICAL_OR_LEGAL =
  /\b(diagnos(?:e|is|ing)|prescrib|medicat(?:e|ion)|dos(?:e|age)|treat my|my patient|my client needs therapy|malpractice|sue|lawyer|attorney|legal advice|hipaa breach| Tarasoff|should i report my)\b/i;

const OFF_TOPIC =
  /\b(bitcoin|crypto|recipe|girlfriend|boyfriend|write (?:me )?a poem|stock market|homework for calculus)\b/i;

export function truncateAskPamPrompt(prompt: string): string {
  return prompt.trim().slice(0, ASK_PAM_PROMPT_MAX);
}

/** Exam-prep only. Clinical/legal/off-topic → calm refuse (not an error). */
export function classifyAskPamPrompt(prompt: string): 'ok' | 'refuse' {
  const text = prompt.trim();
  if (!text) return 'refuse';
  if (CLINICAL_OR_LEGAL.test(text) || OFF_TOPIC.test(text)) return 'refuse';
  if (EXAM_PREP_HINTS.test(text)) return 'ok';
  // Ambiguous short study asks still pass; pure chit-chat without exam cues refuses.
  if (text.length < 12) return 'refuse';
  return 'ok';
}

export const ASK_PAM_REFUSE_MARKDOWN =
  "I'm here for ASWB exam prep: how questions are framed, study strategy, and how to reason through vignettes. " +
  "I can't give clinical treatment advice or legal guidance. " +
  'Try asking about a practice-question stem, a content area, or how to approach a FIRST/NEXT item.';

export const ASK_PAM_DEGRADED_MARKDOWN =
  "Let's keep this exam-focused without the live coach for a moment: " +
  'read the stem carefully, eliminate absolutes, and ask what the question wants FIRST versus BEST. ' +
  'When capacity frees up, ask again and we can go deeper.';

export function simulationFeedback(decisionId: string, beat = PUBLIC_DIAGNOSTIC_BEAT): string {
  return beat.feedbackByDecision[decisionId] ?? beat.defaultFeedback;
}

export function buildSimulationBeatResult(input: {
  scenarioId: string;
  decisionId: string;
  llmUsed: boolean;
  completedAt?: string;
}): SimulationBeatResult {
  return {
    turnId: 3,
    kind: 'simulation_beat',
    completedAt: input.completedAt ?? new Date().toISOString(),
    scenarioId: input.scenarioId,
    decisionId: input.decisionId,
    llmUsed: input.llmUsed,
  };
}

export function buildAskPamResult(input: {
  prompt: string;
  answerMarkdown: string;
  llmUsed: boolean;
  refused?: boolean;
  completedAt?: string;
}): AskPamResult {
  return {
    turnId: 6,
    kind: 'ask_pam',
    completedAt: input.completedAt ?? new Date().toISOString(),
    prompt: truncateAskPamPrompt(input.prompt),
    answerMarkdown: input.answerMarkdown,
    llmUsed: input.llmUsed,
    ...(input.refused ? { refused: true } : {}),
  };
}

/** Sliding-window limiter (session or IP key). */
export class LaneCRateLimiter {
  private hits = new Map<string, number[]>();

  allow(key: string, max: number, windowMs: number, now = Date.now()): boolean {
    const start = now - windowMs;
    const next = (this.hits.get(key) ?? []).filter((t) => t > start);
    if (next.length >= max) {
      this.hits.set(key, next);
      return false;
    }
    next.push(now);
    this.hits.set(key, next);
    return true;
  }

  reset(key?: string): void {
    if (key) this.hits.delete(key);
    else this.hits.clear();
  }
}

export const laneCSessionLimiter = new LaneCRateLimiter();
export const laneCIpLimiter = new LaneCRateLimiter();

/** Daily USD meter — client mirror for tests / degrade; server owns real spend. */
export class LaneCDailySpendMeter {
  private spent = 0;
  constructor(private readonly capUsd: number) {}

  get remaining(): number {
    return Math.max(0, this.capUsd - this.spent);
  }

  get atCap(): boolean {
    return this.spent >= this.capUsd;
  }

  /** Returns false when the call would exceed the cap (caller must degrade). */
  tryCharge(costUsd: number): boolean {
    if (this.spent + costUsd > this.capUsd) return false;
    this.spent += costUsd;
    return true;
  }

  reset(): void {
    this.spent = 0;
  }
}

export function readDailyCapUsd(
  envValue: string | undefined = typeof process !== 'undefined'
    ? process.env.PUBLIC_DIAGNOSTIC_LLM_DAILY_CAP_USD
    : undefined
): number {
  const n = Number(envValue);
  return Number.isFinite(n) && n >= 0 ? n : 5;
}

/** Best-effort anon IP key for client-side mirror (not security-critical). */
export function clientIpKey(): string {
  try {
    return `ip:${window.location.hostname}`;
  } catch {
    return 'ip:unknown';
  }
}
